// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

/**
 * AML Documentation Request domain types.
 *
 * Mirrors resource-server `com.loa.dto.request.aml.*`. Kept in sync with
 * `apps/client-ui/src/types/aml.ts` — changes here should apply there too.
 * See docs/specs/aml-documentation-request-prd.md.
 */

export type AmlDocumentType =
  | 'PASSPORT'
  | 'DRIVING_LICENCE'
  | 'NATIONAL_ID_CARD'
  | 'UTILITY_BILL'
  | 'BANK_STATEMENT'
  | 'TENANCY_AGREEMENT'
  | 'SOURCE_OF_FUNDS'
  | 'SOURCE_OF_WEALTH'
  | 'TAX_REGISTRATION'
  | 'OTHER';

export type AmlRequirementType =
  | 'PROOF_OF_IDENTITY'
  | 'PROOF_OF_ADDRESS'
  | 'SOURCE_OF_FUNDS'
  | 'SOURCE_OF_WEALTH'
  | 'TAX_REGISTRATION'
  | 'OTHER';

export type AmlItemStatus =
  | 'REQUESTED'
  | 'UPLOADED'
  | 'APPROVED'
  | 'REJECTED'
  | 'CANCELLED';

export interface AmlRequestItemDTO {
  id: string;
  requestId: string;
  profileId: string;
  targetClientId: string;
  requirementType: AmlRequirementType;
  /** Human label resolved server-side (e.g. "Proof of identity"). */
  requirementLabel: string;
  /** Document types the client may upload — any ONE satisfies the requirement. */
  acceptedDocumentTypes: AmlDocumentType[];
  /** Human labels for the accepted document types (same order). */
  acceptedDocumentTypeLabels: string[];
  /** Which document the client actually provided (null until uploaded). */
  uploadedDocumentType?: AmlDocumentType | null;
  uploadedDocumentTypeLabel?: string | null;
  note?: string | null;
  status: AmlItemStatus;
  clientDocumentId?: string | null;
  originalFilename?: string | null;
  sizeBytes?: number | null;
  uploadedAt?: string | null;
  rejectionReason?: string | null;
  previousRejectionReason?: string | null;
  renewalOfItemId?: string | null;
  validUntil?: string | null;
  reviewedBy?: string | null;
  reviewedAt?: string | null;
  createdAt: string;
}

export interface AmlDecisionResultDTO {
  item: AmlRequestItemDTO;
  requestStatus: string;
  /** Set when the final decision rejected something — the spawned renewal request. */
  renewalRequestId?: string | null;
}

export interface AmlSummaryEntryDTO {
  clientId: string;
  clientName?: string | null;
  requirementType: AmlRequirementType;
  requirementLabel: string;
  uploadedDocumentType?: AmlDocumentType | null;
  uploadedDocumentTypeLabel?: string | null;
  status: AmlItemStatus;
  itemId: string;
  requestId: string;
  clientDocumentId?: string | null;
  rejectionReason?: string | null;
  reviewedBy?: string | null;
  reviewedAt?: string | null;
  validUntil?: string | null;
  updatedAt?: string | null;
}

/**
 * Requirement metadata for the request modal — mirrors the server's
 * AmlRequirementType. Choice groups (identity / address) list several
 * document types: the advisor picks WHICH are acceptable and the client
 * satisfies the requirement by uploading ANY ONE of them. Specific
 * requirements list exactly one type and are each individually required.
 */
export interface AmlRequirementSpec {
  type: AmlRequirementType;
  label: string;
  documentTypes: ReadonlyArray<{ type: AmlDocumentType; label: string }>;
}

export const AML_REQUIREMENTS: ReadonlyArray<AmlRequirementSpec> = [
  {
    type: 'PROOF_OF_IDENTITY',
    label: 'Proof of identity',
    documentTypes: [
      { type: 'PASSPORT', label: 'Passport' },
      { type: 'DRIVING_LICENCE', label: 'Driving licence' },
      { type: 'NATIONAL_ID_CARD', label: 'National ID card' },
    ],
  },
  {
    type: 'PROOF_OF_ADDRESS',
    label: 'Proof of address',
    documentTypes: [
      { type: 'UTILITY_BILL', label: 'Utility bill' },
      { type: 'BANK_STATEMENT', label: 'Bank statement' },
      { type: 'TENANCY_AGREEMENT', label: 'Tenancy agreement / lease' },
    ],
  },
  {
    type: 'SOURCE_OF_FUNDS',
    label: 'Source of funds evidence',
    documentTypes: [{ type: 'SOURCE_OF_FUNDS', label: 'Source of funds evidence' }],
  },
  {
    type: 'SOURCE_OF_WEALTH',
    label: 'Source of wealth statement',
    documentTypes: [{ type: 'SOURCE_OF_WEALTH', label: 'Source of wealth statement' }],
  },
  {
    type: 'TAX_REGISTRATION',
    label: 'Tax registration evidence',
    documentTypes: [{ type: 'TAX_REGISTRATION', label: 'Tax registration evidence' }],
  },
  {
    type: 'OTHER',
    label: 'Other document',
    documentTypes: [{ type: 'OTHER', label: 'Other document' }],
  },
];
